/**
 * Typed client for the Worker API. Errors arrive as `ApiError` with the
 * server's safe message and per-field messages for forms.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? undefined : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "offline", "You seem to be offline. Check your connection and try again.");
  }
  const text = await response.text();
  const data = text ? (JSON.parse(text) as unknown) : {};
  if (!response.ok) {
    const error = (data as { error?: { code?: string; message?: string; fields?: Record<string, string> } }).error;
    throw new ApiError(response.status, error?.code ?? "error", error?.message ?? "Something went wrong.", error?.fields ?? {});
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body: unknown = {}) => request<T>("POST", path, body),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

// --- Response types (mirror src/server/routes/*) -------------------------------

export interface User {
  id: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
}

export interface Me {
  user: User | null;
  workspaces: { id: string; name: string; role: string; onboarded: boolean }[];
  currentWorkspaceId: string | null;
  /** "Continue with Google" is set up on this server. */
  googleSignIn?: boolean;
  /** False for an account that only signs in with Google. */
  hasPassword?: boolean;
}

export interface Offering {
  name: string;
  priceRupees: number | null;
  unit: string;
}

export interface WorkspaceView {
  workspace: {
    id: string;
    name: string;
    businessType: string | null;
    /** `creator` = a creator workspace (brand deals, content planner, media kit). */
    kind: "business" | "creator";
    city: string | null;
    timezone: string;
    replyLanguage: string;
    autonomyMode: "copilot" | "supervised" | "bounded_autopilot";
    publicFormEnabled: boolean;
    dailyDigestEnabled: boolean;
    publicFormUrl: string;
    onboardingCompletedAt: string | null;
    createdAt: string;
  };
  profile: {
    description: string;
    offerings: Offering[];
    businessHours: string;
    serviceArea: string;
    tone: "warm" | "formal" | "brief";
    faqs: { question: string; answer: string }[];
  };
  subscription: { planId: string; status: string; currentPeriodEnd: string | null; cancelAtPeriodEnd: boolean } | null;
  role: "owner" | "administrator" | "operator" | "viewer";
}

export interface Lead {
  id: string;
  stage: string;
  need: string;
  valueRupees: number | null;
  source: string;
  nextFollowUpAt: string | null;
  outcome: "won" | "lost" | null;
  outcomeVerifiedBy: "owner" | "payment_provider" | null;
  createdAt: string;
  updatedAt: string;
  contact: { id: string; name: string; phone: string | null; email: string | null; instagramUsername?: string | null; consent: string };
  /** Lead intelligence: null until Thekedar has scored the lead. */
  ai?: { score: number; summary: string | null; nextAction: string | null; scoredAt: string | null } | null;
}

export interface ConversationSummary {
  id: string;
  channel: string;
  status: string;
  lastMessageAt: string | null;
  preview: string;
  awaitingReply: boolean;
  hasDraft: boolean;
  contact: { id: string; name: string; phone: string | null; consent: string };
}

export interface Message {
  id: string;
  direction: "inbound" | "outbound";
  author: "contact" | "owner" | "agent" | "system";
  body: string;
  status: string;
  actionIntentId: string | null;
  createdAt: string;
  sentAt: string | null;
}

export interface ConversationDetail {
  conversation: {
    id: string;
    channel: string;
    status: string;
    contact: { id: string; name: string; phone: string | null; email: string | null; instagramUsername?: string | null; consent: string };
    leadId: string | null;
    leadStage: string | null;
  };
  messages: Message[];
  sending: { canSend: boolean; canSendViaWhatsApp: boolean; whatsappConnected: boolean; instagramConnected: boolean; windowOpen: boolean; manualWhatsAppNumber: string | null; optedOut: boolean };
}

export interface Approval {
  id: string;
  status: string;
  title: string;
  summary: string;
  risk: "low" | "medium" | "high";
  requestedBy: string;
  expiresAt: string;
  createdAt: string;
  decidedAt: string | null;
  note: string | null;
  action: { id: string; type: string; channel: string | null; amountRupees: number | null; reasons: string[]; status: string; errorCode: string | null; result: Record<string, unknown> | null };
}

export interface Dashboard {
  enquiriesThisWeek: number;
  awaitingReply: number;
  pendingApprovals: number;
  openTasks: number;
  pipeline: { stage: string; name: string; leads: number; valueRupees: number }[];
  wonThisMonth: { count: number; paymentVerifiedRupees: number; markedByOwner: number };
  medianFirstReplyMinutes: number | null;
  firstReplySample: number;
  usage: { aiPercentUsed: number; aiCreditLeftPaise: number; aiCreditTotalPaise: number; aiTopupCreditPaise: number; aiResetsAt: string; messagesSent: number; paymentLinks: number };
}

export interface Task {
  id: string;
  leadId: string | null;
  title: string;
  detail: string;
  status: string;
  priority: "low" | "normal" | "high";
  agent: string | null;
  dueAt: string | null;
  createdAt: string;
  contactName: string | null;
}

export interface ActivityEvent {
  id: string;
  actor: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  summary: string;
  at: string;
}

export interface IntegrationEntry {
  provider: string;
  name: string;
  summary: string;
  built: boolean;
  connection: {
    id: string;
    status: string;
    displayName: string;
    config: Record<string, unknown>;
    capabilities: string[];
    lastHealthCheckAt: string | null;
    lastError: string | null;
    webhookVerifiedAt?: string | null;
    connectedAt: string;
  } | null;
}

export interface LeadStage {
  key: string;
  name: string;
  outcome: "won" | "lost" | null;
}

export interface LeadDetail {
  lead: Lead;
  conversations: { id: string; channel: string; status: string; lastMessageAt: string | null }[];
  actions: { id: string; type: string; status: string; amountRupees: number | null; result: Record<string, unknown> | null; createdAt: string }[];
  tasks: { id: string; title: string; status: string; dueAt: string | null }[];
  timeline: { action: string; summary: string; actor: string; at: string }[];
}

export interface Policy {
  version: number;
  paused: boolean;
  pausedChannels: string[];
  updatedAt: string | null;
}

export interface Member {
  userId: string;
  displayName: string;
  email: string;
  role: "owner" | "administrator" | "operator" | "viewer";
  joinedAt: string;
  isYou: boolean;
}

export interface Invitation {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  createdAt: string;
}

export interface BillingView {
  pricingApproved: boolean;
  checkout: { available: boolean; reason: string | null };
  plans: BillingPlan[];
  freePlan: BillingPlan;
  /** One sentence on how "ideal for up to N customers" is worked out. */
  estimateRule: string;
  /** Usage credit is 2x the real AI cost; only credit amounts (paise) are sent. Monthly plan credit and top-up balance are separate. */
  ai: {
    creditTotalPaise: number;
    creditUsedPaise: number;
    creditLeftPaise: number;
    topupCreditPaise: number;
    percentUsed: number;
    resetsAt: string;
    freeGate: "locked" | "trial" | "full" | null;
    model: "Claude Haiku 5.5" | "Workers AI (fallback)" | null;
    breakdown: { purpose: string; label: string; paise: number }[];
    topups: { enabled: boolean; reason?: string; rule: string; packs: { pricePaise: number; creditPaise: number }[] };
  };
  subscription: { planId: string; planName: string; status: string; paidPlanId: string; currentPeriodEnd: string | null; cancelAtPeriodEnd: boolean } | null;
  usage: { period: string; messagesSent: number; paymentLinks: number };
}

export interface BillingPlan {
  id: string;
  name: string;
  pricePaise: number;
  seats: number;
  /** Monthly usage credit, in paise. */
  aiCreditPaise: number;
  idealCustomers: number;
  knowledgeDocuments: number;
  features: string[];
}

export interface IntegrationSetup {
  webhookUrl: string;
  verifyToken?: string;
  webhookSecret?: string;
  subscribeFields?: string[];
  events?: string[];
}

export interface ConnectResult {
  connected: boolean;
  webhookUrl: string;
  verifyToken?: string;
  webhookSecret?: string;
  mode?: string;
  username?: string;
  followersCount?: number | null;
  subscribed?: boolean;
  subscribeError?: string | null;
}

export interface Readiness {
  score: number;
  items: { key: "description" | "offerings" | "hours" | "area" | "faqs" | "channel"; done: boolean; title: string; why: string }[];
  customerQuestions: { text: string; askedAt: string }[];
}

/** One employee on this workspace's team (GET /api/employees `team`). `roleId` is the catalog role. */
export interface TeamMember {
  id: string;
  roleId: string;
  name: string;
  title: string;
  hue: [string, string];
  status: "on_duty" | "resting" | "paused";
  today: { drafts: number; followUps: number; digests: number; marketing?: number; cases?: number; bookings?: number; invoices?: number };
  lastWorkedAt: string | null;
  reason: string | null;
}

/** A role the owner can add (GET /api/employees `catalog`). */
export interface CatalogRole {
  roleId: string;
  title: string;
  pitch: string;
  duties: string[];
  hue: [string, string];
  suggestedName: string;
  added: boolean;
}

export interface EmployeesResponse {
  kind: "business" | "creator";
  team: TeamMember[];
  catalog: CatalogRole[];
  limit: { max: number; used: number; plan: string };
}

export interface OvernightSummary {
  since: string;
  employees: { id: string; name: string; title: string; count: number; summaries: string[] }[];
}

// Today ---------------------------------------------------------------

export interface AskSuggestion {
  id: string;
  text: string;
  prompt: string;
  roleId: string;
}

/** Creator workspaces only (null for a business). Figures come from the deals' stored rows and dates. */
export interface TodayDeals {
  active: number;
  owed: { id: string; brand: string; feePaise: number; payment: { state: "none" | "due" | "late" | "paid"; dueOn: string | null; days: number | null; text: string } }[];
  upcoming: { id: string; dealId: string; brand: string; kind: string; title: string; dueOn: string; inDays: number }[];
}

export interface TodayView {
  deals?: TodayDeals | null;
  brief: { state: "ok" | "none" | "no_manager" | "unavailable"; text: string | null; managerName: string | null; generatedAt: string | null; refreshesLeft: number };
  canDecide: boolean;
  hotLeads: { id: string; name: string; need: string; stage: string; phone: string | null; score: number | null; summary: string | null; nextAction: string | null; updatedAt: string }[];
  openLeads: number;
  money: { overdueCount: number; overduePaise: number; overdueRupees: number; unpaidCount: number; unpaidPaise: number; unpaidRupees: number };
  bookings: { id: string; startsAt: string; status: string; customer: string; service: string }[];
  complaints: { open: number; urgent: number; pastTarget: number; items: { id: string; subject: string; priority: string; customer: string; dueAt: string; pastTarget: boolean }[] };
  approvals: { count: number; items: { id: string; title: string; summary: string; risk: string; expiresAt: string }[] };
}

// Marketing -----------------------------------------------------------

export interface MarketingFunnelRow {
  source: string;
  label: string;
  enquiries: number;
  replied: number;
  won: number;
  conversionPct: number | null;
  revenueMinor: number;
}

export interface MarketingTotals {
  enquiries: number;
  replied: number;
  won: number;
  conversionPct: number | null;
  revenueMinor: number;
}

export interface MarketingInsights {
  days: 7 | 30 | 90;
  since: string;
  sources: MarketingFunnelRow[];
  totals: MarketingTotals;
  previous: MarketingTotals;
  replyTimes: { channel: string; medianMinutes: number; samples: number }[];
  /** Monday first, 24 IST hours. */
  heatmap: number[][];
  busiest: { total: number; day: string; windowLabel: string; sharePct: number } | null;
  topQuestions: { text: string; count: number }[];
  instagramDms: { conversations: number; messages: number };
}

export interface MarketingPost {
  id: string;
  caption: string;
  mediaType: string;
  timestamp: string;
  likes: number | null;
  comments: number | null;
  engagement: number;
  permalink: string | null;
}

export type MarketingInstagram =
  | { state: "not_connected" }
  | { state: "degraded" | "error"; message: string }
  | { state: "ok"; posts: MarketingPost[]; fetchedAt: string; cached: boolean; note: string | null };

export interface MarketingCampaign {
  name: string;
  spend: number;
  impressions: number;
  clicks: number;
  ctr: number | null;
  cpc: number | null;
  leads: number;
  conversations: number;
  results: number;
  costPerResult: number | null;
}

export type MarketingAds =
  | { state: "not_connected" }
  | { state: "degraded" | "error"; message: string }
  | {
      state: "ok";
      account: { name: string; currency: string };
      preset: "last_7d" | "last_30d";
      campaigns: MarketingCampaign[];
      totals: { spend: number; impressions: number; clicks: number; ctr: number | null; results: number; costPerResult: number | null };
      fetchedAt: string;
      cached: boolean;
      note: string | null;
    };

export interface MarketingIdeas {
  last: { ideas: { hook: string; caption: string; format: "reel" | "carousel" | "story" }[]; generatedAt: string } | null;
  aiAvailable: boolean;
  allowanceRemaining: number;
  blockedReason: string | null;
}

// Customer Support
export type CaseCategory = "complaint" | "warranty" | "service" | "refund" | "question";
export type CaseStatus = "open" | "waiting_on_customer" | "resolved";

export interface SupportCase {
  id: string;
  subject: string;
  category: CaseCategory;
  status: CaseStatus;
  priority: "normal" | "urgent";
  openedBy: "agent" | "owner";
  dueAt: string;
  overdue: boolean;
  resolvedAt: string | null;
  resolution: string | null;
  conversationId: string | null;
  leadId: string | null;
  contact: { id: string; name: string; phone: string | null };
  createdAt: string;
  updatedAt: string;
}

export interface CaseList {
  hired: boolean;
  counts: { open: number; waiting: number; resolved: number; urgent: number };
  cases: SupportCase[];
}

export interface CaseDetail {
  case: SupportCase;
  events: { id: string; kind: "opened" | "customer_message" | "note" | "status" | "priority" | "resolved" | "overdue"; body: string; by: "customer" | "kabir" | "owner"; createdAt: string }[];
}

export interface SupportSettings {
  hired: boolean;
  checkInDays: number;
  offerings: { name: string; warrantyMonths: number | null }[];
}

// --- Receptionist (bookings) ------------------------------------------

export interface BookingTypeView {
  id: string;
  name: string;
  durationMinutes: number;
  priceNote: string;
}

export interface BookingDayHours {
  open: string;
  close: string;
}

export interface BookingSettingsView {
  hired: boolean;
  saved: boolean;
  settings: { weekly: (BookingDayHours | null)[]; slotMinutes: number; bufferMinutes: number; advanceDays: number; capacity: number; confirmEach: boolean };
  types: BookingTypeView[];
  maxTypes: number;
  bookingUrl: string | null;
  linkEnabled: boolean;
}

export interface CalendarFeedView {
  enabled: boolean;
  url: string | null;
}

export interface AppointmentView {
  id: string;
  status: "requested" | "confirmed" | "cancelled" | "completed" | "no_show";
  source: "public_page" | "chat" | "owner";
  startsAt: string;
  endsAt: string;
  notes: string;
  typeName: string;
  contact: { id: string; name: string; phone: string | null };
  reminderSentAt: string | null;
  reminderLink: string | null;
}

export interface AppointmentList {
  when: "today" | "upcoming" | "past";
  awaitingConfirmation: number;
  appointments: AppointmentView[];
}

export interface PublicBookingInfo {
  business: { name: string; city: string | null };
  types: BookingTypeView[];
  advanceDays: number;
  confirmEach: boolean;
  today: string;
  turnstileSiteKey: string | null;
}

export interface PublicSlots {
  days: { date: string; slots: { startsAt: string; endsAt: string }[] }[];
}

// --- Invoices (the Accountant). Money is integer paise. ----------------------

export interface InvoiceSummary {
  id: string;
  number: string | null;
  status: "draft" | "issued" | "paid" | "cancelled";
  overdue: boolean;
  financialYear: string | null;
  contactId: string | null;
  leadId: string | null;
  customer: { name: string; phone: string | null; email: string | null; gstin: string | null; address: string; state: string };
  issueDate: string | null;
  dueDate: string | null;
  gstRegistered: boolean;
  pricesIncludeGst: boolean;
  subtotalPaise: number;
  cgstPaise: number;
  sgstPaise: number;
  igstPaise: number;
  totalPaise: number;
  amountPaidPaise: number;
  paidAt: string | null;
  paymentSource: "razorpay" | "manual" | null;
  paymentMethod: string | null;
  notes: string;
  remindersSent: number;
  madeBy: string | null;
  createdAt: string;
}

export interface InvoiceLineView {
  id: string;
  description: string;
  hsnSac: string | null;
  quantity: number;
  unitPricePaise: number;
  gstRate: number;
  taxablePaise: number;
  cgstPaise: number;
  sgstPaise: number;
  igstPaise: number;
}

export interface InvoiceList {
  invoices: InvoiceSummary[];
  summary: { count: number; paise: number; overdueCount: number; overduePaise: number };
}

export interface InvoiceDetail {
  invoice: InvoiceSummary;
  lines: InvoiceLineView[];
  shareUrl: string | null;
  seller: { legalName: string } | null;
}

export interface BillingProfileView {
  legalName: string;
  address: string;
  state: string;
  gstin: string | null;
  invoicePrefix: string;
  defaultGstRate: number;
  pricesIncludeGst: boolean;
  paymentTermsDays: number;
  upiId: string | null;
}

export interface BillingDetails {
  profile: BillingProfileView | null;
  states: string[];
  gstRates: number[];
}

export interface InvoiceSendResult {
  mode: "proposed" | "task";
  status?: string;
  waUrl?: string | null;
  text: string;
}

export interface PublicInvoice {
  invoice: {
    number: string;
    status: "issued" | "paid" | "cancelled";
    overdue: boolean;
    financialYear: string;
    issueDate: string | null;
    dueDate: string | null;
    gstRegistered: boolean;
    pricesIncludeGst: boolean;
    subtotalPaise: number;
    cgstPaise: number;
    sgstPaise: number;
    igstPaise: number;
    totalPaise: number;
    amountPaidPaise: number;
    paidAt: string | null;
    notes: string;
  };
  business: { legalName: string; address: string; state: string; gstin: string | null; upiId: string | null };
  customer: { name: string; phone: string | null; email: string | null; gstin: string | null; address: string; state: string };
  lines: InvoiceLineView[];
  upiUri: string | null;
}

// Creator analytics ---------------------------------------------------

export type AnalyticsMetrics = Record<string, number | null>;

export interface AnalyticsSeriesPoint {
  at: string;
  value: number;
}

export interface AnalyticsView {
  instagram: {
    state: "not_connected" | "connected" | "degraded";
    message: string | null;
    username: string | null;
    account: { username: string; accountType: string | null; followers: number | null; permissionMissing: boolean; history: AnalyticsSeriesPoint[] } | null;
    items: { id: string; caption: string; kind: "reel" | "post"; timestamp: string; permalink: string | null; metrics: AnalyticsMetrics; fetchedAt: string }[];
    fetchedAt: string | null;
  };
  youtube: {
    configured: boolean;
    verified: boolean;
    state: "not_connected" | "connected" | "degraded";
    message: string | null;
    channelTitle: string | null;
    account: { channelId: string; title: string; subscribers: number | null; range: { start: string; end: string }; history: AnalyticsSeriesPoint[] } | null;
    items: { id: string; title: string; publishedAt: string; durationSeconds: number | null; metrics: AnalyticsMetrics }[];
    daily: { date: string; metrics: AnalyticsMetrics }[];
    segments: { type: string; metrics: AnalyticsMetrics }[];
    fetchedAt: string | null;
  };
  takeaways: { items: string[]; generatedAt: string } | null;
  itemsWithData: number;
  minTakeawayItems: number;
  aiAvailable: boolean;
}

export interface AnalyticsTakeaways {
  takeaways: { items: string[]; generatedAt: string } | null;
  notEnoughData: boolean;
  count?: number;
  message?: string;
}

/** `GET /api/billing/balance`: the sidebar / top-bar balance (usage credit only). */
export interface CreditBalance {
  planId: string;
  planName: string;
  topPlan: boolean;
  /** Monthly credit left + top-up balance: the figure the meter and Find leads use. */
  availablePaise: number;
  monthlyLeftPaise: number;
  monthlyTotalPaise: number;
  topupCreditPaise: number;
  resetsAt: string;
  freeGate: "locked" | "trial" | "full" | null;
  low: boolean;
  canTopUp: boolean;
  topups: { enabled: boolean; reason?: string; packs: { pricePaise: number; creditPaise: number }[] };
}

// Find leads (Phase 22): mirrors routes/lead-finder.ts.
export interface LeadSearch {
  id: string;
  tier: string;
  tierLabel: string;
  modelLabel: string;
  status: "running" | "done" | "short" | "failed";
  minimumStrong: number;
  pricePaise: number;
  charged: boolean;
  strongFound: number;
  leadsFound: number;
  goodFitsFound: number;
  roundsDone: number;
  roundsTotal: number;
  leftOut: number;
  /** A free test-site run judged by Workers AI (never charged). */
  testRun: boolean;
  /** Per-source tallies: rounds, results fetched, candidates the model named, leads kept after the checks, strong ones. */
  sources: Record<string, { rounds: number; fetched: number; candidates: number; kept: number; strong: number; dated?: number }> | null;
  message: string;
  createdAt: string;
  finishedAt: string | null;
}

export interface LeadOutreach {
  id: string;
  status: string;
  text: string;
  error: string | null;
  approvalId: string | null;
  approvalStatus: string | null;
  intentStatus: string | null;
  createdAt: string | null;
  sentAt: string | null;
}

export interface FoundLead {
  id: string;
  kind: "raised_hand" | "good_fit";
  /** Where it came from: web search, YouTube comments or OpenStreetMap. */
  source: "web" | "youtube" | "osm";
  category: string;
  address: string;
  website: string;
  mapsUrl: string | null;
  rank: number | null;
  rankChange: number | null;
  isNew: boolean;
  strong: boolean;
  score: number;
  url: string;
  host: string;
  title: string;
  author: string;
  quote: string;
  signal: string;
  need: string;
  why: string;
  postedOn: string | null;
  location: string;
  status: "new" | "contacted";
  firstFoundAt: string;
  lastSeenAt: string;
  contactedAt: string | null;
  outreach: LeadOutreach | null;
}

export interface LeadTierView {
  id: string;
  label: string;
  modelLabel: string;
  minimumStrong: number;
  pricePaise: number;
  pitch: string;
  blocked: { code: string; message: string } | null;
}

/** `GET /api/lead-finder?poll=1` carries only these; the full answer adds the rest. */
export interface LeadFinderPoll {
  kind: "business" | "creator";
  latest: LeadSearch | null;
  leads: FoundLead[];
  searchesToday: number;
}

export interface GoodFitView {
  catalog: { id: string; label: string }[];
  chosen: string[];
  isDefault: boolean;
  max: number;
  attribution: { text: string; url: string };
}

export interface LeadFinderView extends LeadFinderPoll {
  goodFit: GoodFitView;
  /** Sources of raised hands; `on` only when set up on this server. */
  sources: { id: "web" | "youtube" | "tavily" | "osm"; label: string; on: boolean }[];
  tiers: LeadTierView[];
  employee: { name: string; title: string } | null;
  setup: { code: string; message: string } | null;
  creditLeftPaise: number | null;
  limits: { searchesPerDay: number; maxLeads: number; outreachPerRequest: number; outreachPerDay: number };
  /** Test site only (null in production): free runs judged by Workers AI over YouTube / Tavily. */
  testRun: { rounds: number; neuronsLeft: number; neuronsPerDay: number; runsPerDay: number } | null;
}
